import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trash2, Plus, Minus, ArrowLeft, ShoppingBag, CreditCard } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useCart, useAuth } from "../App";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CartPage() {
  const { cart, updateCartItem, removeFromCart, clearCart } = useCart();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [shippingAddress, setShippingAddress] = useState({
    full_name: "",
    address: "",
    city: "",
    postal_code: "",
    country: "Italia",
    phone: ""
  });
  const [saveShippingAddress, setSaveShippingAddress] = useState(true);

  useEffect(() => {
    const fetchSavedShippingAddress = async () => {
      if (!user) return;
      try {
        const response = await axios.get(`${API}/user/shipping-address`, { withCredentials: true });
        const saved = response.data?.shipping_address;
        if (saved) {
          setShippingAddress({
            full_name: saved.full_name || "",
            address: saved.address || "",
            city: saved.city || "",
            postal_code: saved.postal_code || "",
            country: saved.country || "Italia",
            phone: saved.phone || "",
          });
        }
      } catch (err) {
        console.error("Error fetching saved shipping address:", err);
      }
    };

    fetchSavedShippingAddress();
  }, [user]);

  const handleQuantityChange = async (item, delta) => {
    const newQuantity = item.quantity + delta;
    if (newQuantity <= 0) {
      await removeFromCart(item.product_id, item.size, item.color);
      toast.info("Articolo rimosso dal carrello");
    } else {
      await updateCartItem(item.product_id, item.size, item.color, newQuantity);
    }
  };

  const handleRemove = async (item) => {
    await removeFromCart(item.product_id, item.size, item.color);
    toast.info("Articolo rimosso dal carrello");
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    if (!user) {
      login();
      return;
    }

    // Validate form
    if (!shippingAddress.full_name || !shippingAddress.address || 
        !shippingAddress.city || !shippingAddress.postal_code) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setCheckoutLoading(true);
    
    try {
      if (saveShippingAddress) {
        await axios.put(
          `${API}/user/shipping-address`,
          shippingAddress,
          { withCredentials: true }
        );
      }

      const response = await axios.post(
        `${API}/payments/checkout`,
        {
          shipping_address: shippingAddress,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      // Redirect to Stripe
      window.location.href = response.data.url;
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error("Errore durante il checkout. Riprova.");
      setCheckoutLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-white" data-testid="cart-empty">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-8 border-4 border-black bg-gray-100 flex items-center justify-center">
              <ShoppingBag size={48} className="text-gray-400" />
            </div>
            <h1 className="font-heading text-4xl mb-4">CARRELLO VUOTO</h1>
            <p className="font-body text-lg text-gray-600 mb-8">
              Non hai ancora aggiunto nessun prodotto
            </p>
            <Link to="/magliette" className="btn-brutal inline-flex items-center">
              INIZIA LO SHOPPING
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="cart-page">
      {/* Header */}
      <div className="border-b-4 border-black py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/abbigliamento"
            className="inline-flex items-center font-mono text-sm hover:text-accent-pink"
          >
            <ArrowLeft size={16} className="mr-2" />
            CONTINUA LO SHOPPING
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-heading text-5xl mb-8">IL TUO CARRELLO</h1>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-6">
            {cart.items.map((item, index) => (
              <motion.div
                key={`${item.product_id}-${item.size}-${item.color}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border-2 border-black p-4 flex gap-4"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Image */}
                <Link to={`/prodotto/${item.product_id}`} className="flex-shrink-0">
                  <div className="w-24 h-24 sm:w-32 sm:h-32 border-2 border-black overflow-hidden">
                    <img
                      src={item.product?.image_url}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Link>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <Link to={`/prodotto/${item.product_id}`}>
                    <h3 className="font-heading text-lg sm:text-xl hover:text-accent-pink truncate">
                      {item.product?.name}
                    </h3>
                  </Link>
                  <p className="font-mono text-sm text-gray-500 mt-1">
                    {item.size} / {item.color}
                  </p>
                  <p className="font-mono text-lg font-bold mt-2">
                    €{item.product?.price.toFixed(2)}
                  </p>

                  {/* Quantity Controls */}
                  <div className="flex items-center gap-4 mt-4">
                    <div className="flex items-center border-2 border-black">
                      <button
                        onClick={() => handleQuantityChange(item, -1)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="w-10 text-center font-mono text-sm">
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => handleQuantityChange(item, 1)}
                        className="w-8 h-8 flex items-center justify-center hover:bg-gray-100"
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    <button
                      onClick={() => handleRemove(item)}
                      className="p-2 text-red-600 hover:bg-red-50 border-2 border-transparent hover:border-red-600"
                      data-testid={`remove-${item.product_id}`}
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                {/* Subtotal */}
                <div className="hidden sm:block text-right">
                  <p className="font-mono text-xs text-gray-500 uppercase">Subtotale</p>
                  <p className="font-mono text-xl font-bold">
                    €{item.subtotal?.toFixed(2)}
                  </p>
                </div>
              </motion.div>
            ))}

            {/* Clear Cart */}
            <button
              onClick={() => {
                clearCart();
                toast.info("Carrello svuotato");
              }}
              className="font-mono text-sm text-red-600 hover:underline"
              data-testid="clear-cart-btn"
            >
              Svuota carrello
            </button>
          </div>

          {/* Order Summary */}
          <div>
            <div className="border-4 border-black p-6 sticky top-32">
              <h2 className="font-heading text-2xl mb-6">RIEPILOGO ORDINE</h2>

              <div className="space-y-3 border-b-2 border-black pb-6 mb-6">
                <div className="flex justify-between font-body">
                  <span>Subtotale</span>
                  <span>€{cart.total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-body">
                  <span>Spedizione</span>
                  <span className={cart.total >= 50 ? "text-green-600" : ""}>
                    {cart.total >= 50 ? "GRATIS" : "€5.00"}
                  </span>
                </div>
              </div>

              <div className="flex justify-between mb-6">
                <span className="font-heading text-xl">TOTALE</span>
                <span className="font-mono text-2xl font-bold">
                  €{(cart.total + (cart.total >= 50 ? 0 : 5)).toFixed(2)}
                </span>
              </div>

              {!showCheckoutForm ? (
                <button
                  onClick={() => {
                    if (!user) {
                      login();
                    } else {
                      setShowCheckoutForm(true);
                    }
                  }}
                  className="w-full btn-brutal py-4 flex items-center justify-center gap-2"
                  data-testid="proceed-checkout-btn"
                >
                  <CreditCard size={20} />
                  {user ? "PROCEDI AL CHECKOUT" : "ACCEDI PER CONTINUARE"}
                </button>
              ) : (
                <form onSubmit={handleCheckout} className="space-y-4">
                  <h3 className="font-heading text-lg">INDIRIZZO DI SPEDIZIONE</h3>
                  
                  <div>
                    <Label htmlFor="full_name" className="font-mono text-xs">NOME COMPLETO *</Label>
                    <Input
                      id="full_name"
                      value={shippingAddress.full_name}
                      onChange={(e) => setShippingAddress({...shippingAddress, full_name: e.target.value})}
                      className="border-2 border-black rounded-none"
                      required
                      data-testid="shipping-name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address" className="font-mono text-xs">INDIRIZZO *</Label>
                    <Input
                      id="address"
                      value={shippingAddress.address}
                      onChange={(e) => setShippingAddress({...shippingAddress, address: e.target.value})}
                      className="border-2 border-black rounded-none"
                      required
                      data-testid="shipping-address"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city" className="font-mono text-xs">CITTÀ *</Label>
                      <Input
                        id="city"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                        className="border-2 border-black rounded-none"
                        required
                        data-testid="shipping-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal_code" className="font-mono text-xs">CAP *</Label>
                      <Input
                        id="postal_code"
                        value={shippingAddress.postal_code}
                        onChange={(e) => setShippingAddress({...shippingAddress, postal_code: e.target.value})}
                        className="border-2 border-black rounded-none"
                        required
                        data-testid="shipping-postal"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="font-mono text-xs">TELEFONO</Label>
                    <Input
                      id="phone"
                      value={shippingAddress.phone}
                      onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                      className="border-2 border-black rounded-none"
                      data-testid="shipping-phone"
                    />
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={saveShippingAddress}
                      onChange={(e) => setSaveShippingAddress(e.target.checked)}
                      className="w-4 h-4 border-2 border-black"
                    />
                    <span className="font-body text-sm">
                      Salva queste informazioni per i prossimi ordini
                    </span>
                  </label>

                  <button
                    type="submit"
                    disabled={checkoutLoading}
                    className={`
                      w-full py-4 font-heading text-lg border-4 border-black
                      flex items-center justify-center gap-2
                      ${checkoutLoading 
                        ? 'bg-gray-200 cursor-not-allowed' 
                        : 'bg-accent-yellow hover:shadow-brutal'
                      }
                    `}
                    data-testid="checkout-submit-btn"
                  >
                    {checkoutLoading ? (
                      <div className="w-5 h-5 border-2 border-black border-t-transparent animate-spin"></div>
                    ) : (
                      <>
                        <CreditCard size={20} />
                        PAGA ORA
                      </>
                    )}
                  </button>
                </form>
              )}

              {cart.total < 50 && (
                <p className="mt-4 font-mono text-xs text-center text-gray-500">
                  Aggiungi €{(50 - cart.total).toFixed(2)} per la spedizione gratuita
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
